import { AlertTriangle } from 'lucide-react';

import type { PositionReplayDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';

import { ReplayCandlestickChart } from './ReplayCandlestickChart';
import { ReplayExecutionList } from './ReplayExecutionList';

interface TradeReplayChartProps {
  currency: string;
  replay: PositionReplayDto;
  onScopeChange: (scope: 'current' | 'full') => void;
}

export function TradeReplayChart({ currency, onScopeChange, replay }: TradeReplayChartProps) {
  return (
    <section className="space-y-4 rounded border border-border bg-surface p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Trade replay</h2>
          <p className="text-sm text-text-muted">
            Split-adjusted daily OHLCV with executions plotted at their recorded price.
          </p>
        </div>
        <Button
          aria-label={replay.scope === 'full' ? 'Show current episode' : 'Show full symbol history'}
          type="button"
          variant="secondary"
          onClick={() => onScopeChange(replay.scope === 'full' ? 'current' : 'full')}
        >
          {replay.scope === 'full' ? 'Show current episode' : 'Show full history'}
        </Button>
      </div>

      {replay.shortResolution === 'AMBIGUOUS_HISTORY' ? (
        <div
          className="flex gap-3 rounded border border-warn/60 bg-warn-subtle p-4 text-amber-100"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="text-sm">
            This history begins with negative inventory. The position remains signed and visible,
            but needs manual review before its risk and P&amp;L are treated as trusted.
          </p>
        </div>
      ) : null}

      {replay.bars.length > 0 ? (
        <ReplayCandlestickChart
          bars={replay.bars}
          currency={currency}
          episodes={replay.episodes}
          executions={replay.executions}
          symbol={replay.symbol}
        />
      ) : (
        <div className="rounded border border-border bg-bg-muted p-4 text-sm text-text-muted">
          No split-adjusted daily bars are available for {replay.symbol}. Executions are listed
          below.
        </div>
      )}

      <ReplayExecutionList currency={currency} replay={replay} />
    </section>
  );
}
